import { useEffect, useRef } from "react";

interface TurnstileRenderParams {
	sitekey: string;
	callback: (token: string) => void;
	theme?: "light" | "dark" | "auto" | (string & {});
	size?: "normal" | "compact" | (string & {});
	retry?: "auto" | "never";
	retryInterval?: number;
	[key: string]: unknown;
}

interface TurnstileApi {
	render: (
		element: HTMLElement,
		params: TurnstileRenderParams
	) => string | unknown;
	reset?: (id?: string) => void;
	remove?: (id?: string) => void;
}

declare global {
	interface Window {
		turnstile?: TurnstileApi;
	}
}

interface CaptchaProps {
	onVerify: (token: string) => void;
	options?: Record<string, unknown>;
	className?: string;
}

function Captcha({ onVerify, options = {}, className }: CaptchaProps) {
	const siteKey = import.meta.env.VITE_CLOUDFLARE_CAPTCHA_SITE_KEY as
		| string
		| undefined;

	const containerRef = useRef<HTMLDivElement | null>(null);
	const widgetIdRef = useRef<string | unknown | null>(null);

	const render = () => {
		if (typeof window === "undefined") return;
		if (!window.turnstile || !containerRef.current) return;
		if (widgetIdRef.current) return;
		try {
			widgetIdRef.current = window.turnstile.render(containerRef.current, {
				sitekey: siteKey as string,
				callback: (token: string) => onVerify(token),
				theme: (options as TurnstileRenderParams).theme || "auto",
				size: (options as TurnstileRenderParams).size || "normal",
				retry: "auto",
				retryInterval: 8000,
				...options,
			});
		} catch {
			// swallow render errors silently, could add logging
		}
	};

	useEffect(() => {
		if (!siteKey) return;
		if (typeof window === "undefined") return;

		const scriptSrc = "https://challenges.cloudflare.com/turnstile/v0/api.js";
		const existing = document.querySelector(`script[src="${scriptSrc}"]`);
		if (!existing) {
			const script = document.createElement("script");
			script.src = scriptSrc;
			script.async = true;
			script.defer = true;
			script.onload = () => render();
			document.head.appendChild(script);
		}

		let interval: number | undefined;
		if (window.turnstile) {
			render();
		} else {
			interval = window.setInterval(() => {
				if (window.turnstile) {
					render();
					if (interval) window.clearInterval(interval);
				}
			}, 200);
		}
		return () => {
			if (interval) window.clearInterval(interval);
		};
		// We intentionally exclude `options` object reference to avoid reloading script unnecessarily.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [siteKey, onVerify]);

	if (!siteKey) {
		return (
			<div className={className}>
				<p className="text-xs text-red-500">
					Something went wrong while loading the Captcha.
				</p>
			</div>
		);
	}

	return (
		<div className={className}>
			<div ref={containerRef} className="cf-turnstile" />
		</div>
	);
}

export default Captcha;
